import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import useDraft from "@/lib/useDraft";
import { toast } from "sonner";
import { Upload, Download, Plus, FileSpreadsheet, Trash2 } from "lucide-react";

export default function DailyEntries() {
  const [entries, setEntries] = useState([]);
  const [preset, setPreset] = useState("this_month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const fileRef = useRef(null);
  const [showCreate, setShowCreate] = useState(false);

  // Compute date range from preset
  const computeRange = (p) => {
    const now = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const startOfWeek = (d) => { const x = new Date(d); const day = x.getDay() || 7; x.setDate(x.getDate() - day + 1); return x; };
    switch (p) {
      case "today": return { start: fmt(now), end: fmt(now) };
      case "yesterday": { const y = new Date(now); y.setDate(y.getDate() - 1); return { start: fmt(y), end: fmt(y) }; }
      case "this_week": return { start: fmt(startOfWeek(now)), end: fmt(now) };
      case "last_week": { const lw = new Date(now); lw.setDate(lw.getDate() - 7); const s = startOfWeek(lw); const e = new Date(s); e.setDate(s.getDate() + 6); return { start: fmt(s), end: fmt(e) }; }
      case "this_month": return { start: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), end: fmt(now) };
      case "last_month": { const s = new Date(now.getFullYear(), now.getMonth() - 1, 1); const e = new Date(now.getFullYear(), now.getMonth(), 0); return { start: fmt(s), end: fmt(e) }; }
      case "last_7": { const s = new Date(now); s.setDate(s.getDate() - 6); return { start: fmt(s), end: fmt(now) }; }
      case "last_30": { const s = new Date(now); s.setDate(s.getDate() - 29); return { start: fmt(s), end: fmt(now) }; }
      case "this_year": return { start: fmt(new Date(now.getFullYear(), 0, 1)), end: fmt(now) };
      case "all": return { start: "", end: "" };
      default: return { start: "", end: "" };
    }
  };

  useEffect(() => {
    if (preset !== "custom") {
      const r = computeRange(preset);
      setStart(r.start); setEnd(r.end);
    }
  }, [preset]);

  const load = async () => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const { data } = await api.get("/daily-entries", { params });
    setEntries(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/daily-entries/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} rows`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    e.target.value = "";
  };

  const downloadFile = async (url, name) => {
    const token = localStorage.getItem("session_token");
    const res = await api.get(url, { params: token ? { auth: token } : {}, responseType: "blob" });
    const blob = new Blob([res.data]);
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = name; a.click();
    URL.revokeObjectURL(u);
  };

  const del = async (id) => {
    if (!confirm("Delete entry?")) return;
    await api.delete(`/daily-entries/${id}`);
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">DAILY LOG</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Daily Task Entries</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">Date · Project · Given vs Taken · Efficiency</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onImport} className="hidden" data-testid="daily-import-input" />
          <button onClick={() => fileRef.current?.click()} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="daily-import-btn">
            <Upload className="w-3.5 h-3.5" /> IMPORT
          </button>
          <button onClick={() => downloadFile("/daily-entries/template", "daily_entries_template.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="daily-template-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /> TEMPLATE
          </button>
          <button onClick={() => downloadFile(`/daily-entries/export?format=xlsx${start?`&start=${start}`:''}${end?`&end=${end}`:''}`, "daily_entries.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="daily-export-btn">
            <Download className="w-3.5 h-3.5" /> EXPORT
          </button>
          <button onClick={() => setShowCreate(true)} className="brut-btn px-3 py-2 text-xs flex items-center gap-1" data-testid="daily-create-btn">
            <Plus className="w-3.5 h-3.5" /> NEW
          </button>
        </div>
      </div>

      <div className="brut-card p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <div className="label-mono mb-1">RANGE</div>
            <select value={preset} onChange={(e) => setPreset(e.target.value)} className="brut-input px-3 py-1.5 text-sm font-mono min-w-[180px]" data-testid="daily-range-preset">
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="last_7">Last 7 Days</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="last_30">Last 30 Days</option>
              <option value="this_year">This Year</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range…</option>
            </select>
          </div>
          {preset === "custom" && (
            <>
              <div>
                <div className="label-mono mb-1">FROM</div>
                <input type="date" className="brut-input px-3 py-1.5 text-sm font-mono" value={start} onChange={(e) => setStart(e.target.value)} data-testid="daily-start-filter" />
              </div>
              <div>
                <div className="label-mono mb-1">TO</div>
                <input type="date" className="brut-input px-3 py-1.5 text-sm font-mono" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="daily-end-filter" />
              </div>
            </>
          )}
          <div className="text-xs font-mono text-zinc-500 ml-auto">
            {start && end ? <>SHOWING <b>{start}</b> → <b>{end}</b></> : "SHOWING ALL"}
          </div>
        </div>
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-zinc-900 text-white">
              <Th>Date</Th><Th>Project</Th><Th>Job #</Th><Th>User</Th>
              <Th align="right">Given</Th><Th align="right">Taken</Th><Th align="right">Efficiency</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-zinc-400 font-mono text-xs">NO ENTRIES — IMPORT EXCEL OR CREATE ENTRIES</td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`daily-row-${e.id}`}>
                <Td><span className="font-mono">{e.date}</span></Td>
                <Td>{e.project_name}</Td>
                <Td><span className="font-mono text-xs">{e.job_number || "—"}</span></Td>
                <Td>{e.user_name || "—"}</Td>
                <Td align="right"><span className="font-mono">{e.given_time_hours}h</span></Td>
                <Td align="right"><span className="font-mono">{e.taken_time_hours}h</span></Td>
                <Td align="right">
                  <span className={`font-mono font-bold px-2 py-0.5 border border-zinc-900 ${e.efficiency >= 100 ? "bg-green-200" : e.efficiency >= 70 ? "bg-yellow-200" : "bg-red-200"}`}>
                    {e.efficiency}%
                  </span>
                </Td>
                <Td>
                  <button onClick={() => del(e.id)} className="text-red-600 hover:bg-red-50 p-1" data-testid={`delete-daily-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm, clearDraft] = useDraft("daily-entry", "new", { date: "", project_name: "", job_number: "", team: "", user_id: "", user_name: "", given_time_hours: 0, taken_time_hours: 0, notes: "" });
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    api.get("/team/teams").then(({ data }) => setTeams(data?.teams || [])).catch(() => {});
  }, []);

  const membersForTeam = (() => {
    if (!form.team) return [];
    const t = teams.find((x) => x.team === form.team);
    return t?.members || [];
  })();

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Strip the helper `team` field — daily entries don't persist team directly.
      const { team, ...payload } = form;
      await api.post("/daily-entries", payload);
      toast.success("Entry created");
      clearDraft();
      onCreated();
      onClose();
    }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="brut-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-heading text-2xl font-black">New Daily Entry</h2>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">DRAFT AUTO-SAVED · Outside-click won't dismiss</div>
          </div>
          <button
            type="button"
            onClick={() => { if (confirm("Discard unsaved draft?")) { clearDraft(); onClose(); } }}
            className="brut-btn-secondary px-2 py-1 text-[10px] font-mono"
            title="Discard the auto-saved draft and close"
          >DISCARD</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="DATE"><input required type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></Field>
          <Field label="PROJECT NAME"><input required className="brut-input w-full px-3 py-2 text-sm" value={form.project_name} onChange={(e) => setForm({...form, project_name: e.target.value})} /></Field>
          <Field label="JOB NUMBER"><input className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.job_number} onChange={(e) => setForm({...form, job_number: e.target.value})} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="TEAM">
              <select
                className="brut-input w-full px-3 py-2 text-sm font-mono"
                value={form.team}
                onChange={(e) => setForm({ ...form, team: e.target.value, user_id: "", user_name: "" })}
                data-testid="daily-team-select"
              >
                <option value="">— select team —</option>
                {teams.map((t) => <option key={t.team} value={t.team}>{t.team} ({t.member_count})</option>)}
              </select>
            </Field>
            <Field label="TEAM MEMBER">
              <select
                className="brut-input w-full px-3 py-2 text-sm"
                value={form.user_id}
                onChange={(e) => {
                  const m = membersForTeam.find((x) => x.user_id === e.target.value);
                  setForm({ ...form, user_id: e.target.value, user_name: m?.name || "" });
                }}
                disabled={!form.team}
                data-testid="daily-member-select"
              >
                <option value="">{form.team ? "— select member —" : "Pick team first"}</option>
                {membersForTeam.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="GIVEN (HRS)"><input type="number" step="0.25" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.given_time_hours} onChange={(e) => setForm({...form, given_time_hours: parseFloat(e.target.value) || 0})} /></Field>
            <Field label="TAKEN (HRS)"><input type="number" step="0.25" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.taken_time_hours} onChange={(e) => setForm({...form, taken_time_hours: parseFloat(e.target.value) || 0})} /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
            <button type="submit" className="brut-btn px-4 py-2 text-sm" data-testid="submit-daily-btn">CREATE</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Th = ({ children, align = "left" }) => <th className={`text-${align} px-3 py-2 label-mono text-white border-b border-zinc-900 whitespace-nowrap`}>{children}</th>;
const Td = ({ children, align = "left" }) => <td className={`text-${align} px-3 py-2.5`}>{children}</td>;
const Field = ({ label, children }) => <label className="block"><div className="label-mono mb-1">{label}</div>{children}</label>;
